import { appConfig } from '../../constants/app-config';
import { getImpersonateId } from '../tracker-service/tracker-service';

import './exception-handler.less';
import { toSentrySafeObject } from '../../utils/object-utils/object-utils';

let oldOnerror = window.onerror;
let booted = false;
let doc = window.document;
window.onerror = prebootError;
// used because we have to re-throw errors to see them with source maps
let lastException = null;

function prebootError(msg /*, url, line, col, error*/) {
  if (oldOnerror) {
    oldOnerror.apply(this, arguments);
  }
  if (!booted && doc) {
    let appName = '';
    if (window.config && window.config.appName) {
      appName = encodeURIComponent(window.config.appName + ' ');
    }
    // wipe out the body content
    doc.body.innerHTML = `\
<div class="loading-exception">
	<h2>Error: Unable to start the application!</h2>
	<p>We were unable to get the application started! You can try reloading the page, or <a href="mailto:team@qualified.io?subject=${appName}Application%20Startup%20Issues" target="_blank">contact the team at Qualified.</a></p>
	<p>The error that occurred is:</p>
	<pre><code>${msg}</code></pre>
</div>\
`;
  }
}

function isPlainObject(obj) {
  return (
    angular.isObject(obj) && Object.getPrototypeOf(obj) === Object.prototype
  );
}

function sentryOpts(args) {
  let opts = {};
  if (args.length) {
    if (angular.isString(args[0])) {
      opts.extra = { extraMessage: args.shift() };
    } else if (angular.isNumber(args[0].status) || isPlainObject(args[0])) {
      // capture additional data if this is an $http request or it's a plain object
      opts.extra = toSentrySafeObject(args.shift());
    }
  }
  return opts;
}

function sendToSentry($log, ...args) {
  try {
    if (window.Sentry && (!window.config || !window.config.devMode)) {
      args = args.filter(angular.identity);
      let sent = false;
      // handle simple string arguments
      if (args.length && angular.isString(args[0])) {
        window.Sentry.captureMessage(args.shift(), sentryOpts(args));
        sent = true;
      }
      // handle Error arguments (we might capture 2 events)
      if (args.length && args[0] instanceof Error) {
        window.Sentry.captureException(args.shift(), sentryOpts(args));
        sent = true;
      }
      // handle anything else
      if (!sent) {
        window.Sentry.captureMessage('Non-Error Logged', sentryOpts(args));
      }
    }
  } catch (e) {
    $log.warn('Error logging failure:');
    $log.warn(e);
  }
}

export const exceptionHandlerServiceModule = angular
  .module('Q.services.exception-handler', [])

  .run(function ($log, $rootScope, $injector, $q) {
    booted = true;
    if (window.onerror === prebootError) {
      window.onerror = oldOnerror;
    } // otherwise, someone else overwrote the onerror, so we just leave it

    // wrap the built-in $log.error function so it sends it to Sentry (after booting)
    let oldError = $log.error;
    $log.error = function (...args) {
      oldError.apply($log, args);
      sendToSentry($log, ...args);
    };

    $log.reject = (...args) => {
      $log.error(...args);
      return $q.reject(args[0]);
    };

    if (window.Sentry) {
      window.Sentry.setTags({
        appName: appConfig.appName,
        env: appConfig.env,
      });
      $rootScope.$on('app.accountSet', ($event, account) => {
        if (account && account.user) {
          let user = account.user;
          window.Sentry.setUser({
            id: user.id,
            email: user.email,
          });
          let impersonateId = getImpersonateId();
          if (impersonateId) {
            // track that this is an impersonated session
            window.Sentry.setTags({ impersonateId });
          }
        }
      });
      $rootScope.$on('app.logout', () => window.Sentry.setUser());
    }
  })

  .factory('$exceptionHandler', function ($log, $injector) {
    return function log(exception, cause) {
      if (lastException === exception) return;

      if (cause) {
        try {
          exception.message += ' (caused by "' + cause + '")';
        } catch (e) {
          $log.error('Cause: ', cause);
          // we cannot modify the message without error sometimes
        }
      }
      $log.error(exception);

      if (!booted) {
        // send any uncaught errors to Sentry directly before booting
        sendToSentry($log, exception);
      }

      if (appConfig.devMode && $injector.has('$state')) {
        try {
          const $state = $injector.get('$state');
          $state.go('error', { error: exception, didLog: true });
        } catch (e) {}
      }

      lastException = exception;
      if (exception.stack) {
        // re-throw so we get stack traces with source maps
        // eslint-disable-next-line angular/timeout-service
        setTimeout(() => {
          throw exception;
        });
      }
    };
  });
